import React, { useCallback, useEffect, useMemo, useState } from "react";
import Grid from "../../components/Grid";
import {useAuthWithRoute, useUrlState} from "../../utils/hooks";
import {useVsiAppContext} from "../../stores/VsiApp";
import {VsiH4, VsiBubble} from "../../styled/App.styled";
import {PostAuth, PutAuth} from "../../utils/Verbs";

import {Card, Nav, NavItem, NavLink} from "reactstrap";
import {
    storageTaggingDefs,
    virtualClusterTaggingDefs,
    switchTaggingDefs,
    cloudResourceTaggingDefs,
    databaseInstanceTaggingDefs,
    virtualEsxHostTaggingDefs
} from "../../utils/gridColumnDefs/user/taggingDefs";
import {VsiSelectLabel} from "../../styled/VsiLabeledSelect.styled";
import { showAlert } from "../../stores/Alerts";

const allTagContexts = [
    {value: "storage", label: "Storage Devices"},
    {value: "virtual_cluster", label: "Virtual Clusters"},
    {value: "esx_host", label: "Virtual Esx Hosts"},
    {value: "switch", label: "Switches"},
    {value: "database_instance", label: "Database Instances"},
    {value: "cloud_resource", label: "Cloud Resource Groups"}
];

export default React.memo(function Tagging() {
    const {theme, client, getAccessTokenSilently } = useVsiAppContext();
    const [params, setParams] = useUrlState({
        context: "storage"
    });

    const { context } = params;

    let route = "";

    if (context === "storage") {
        route = "bu_san_capacities";
    } else if (context === "virtual_cluster") {
        route = "virtual_cluster_tags";
    } else if (context === "esx_host") {
        route = "virtual_esx_host_tags";
    } else if (context === "switch") {
        route = "switch_tags";
    } else if (context === "database_instance") {
        route = "database_instance_tags";
    } else if (context === "cloud_resource") {
        route = "cloud_resource_tags";
    }

    const [buData, { isLoading }] = useAuthWithRoute(`/api/v6/admin/${route}/client/${client.clientId}`, route);

    const putBusc = useCallback(async (id, data) => {
        try {
            const putRoute = context !== "storage" ? route.slice(0, -1) : route;
            const token = await getAccessTokenSilently();
            const response = await PutAuth(`/api/v6/admin/${putRoute}/${id}`, data, token);

            if (response && response.status === 200) {
                // leaving this here in case it causes problems later
                // setData(buData => buData.map(datum => {
                //     if (datum._id === id) {
                //         return {
                //             ...datum,
                //             ...data
                //         };
                //     }
                //     return datum;
                // }));
            }
            showAlert(`${data?.arrayName ?? data?.deviceName ?? data?.clusterName ?? data?.switchName ?? data?.instanceName ?? data?.resourceName ?? 'Record'} updated!`, "success");
            return response;
        } catch (error) {
            console.error(error);
            showAlert("Something went wrong. Please try again.", "danger");
            return error;
        }
    }, [context, route, getAccessTokenSilently]);

    const postBusc = useCallback(async (data) => {
        try {

            const postRoute = context !== "storage" ? route.slice(0, -1) : route;
            const token = await getAccessTokenSilently();
            const response = await PostAuth(`/api/v6/admin/${postRoute}`, data, token);
            if (response && response.status === 200) {
                showAlert("Record saved successfully.", "success");
            } else {
                showAlert("Something went wrong. Please try again.", "warning");
            }
            return response;
        } catch (error) {
            console.error(error);
            return error;
        }
    }, [context, route, getAccessTokenSilently]);

    const onRowEditingStopped = useCallback(async (event) => {
        // update busc to Mongo after a cell is edited
        const updatedData = {...event.data};
        Object.keys(event.data).forEach(key => {
            if (updatedData[key] === "undefined" || !updatedData[key]) {
                delete updatedData[key];
            }
        });

        const token = await getAccessTokenSilently();
        if (context === "storage") {
            postBusc(updatedData).then(res => {
                if (res.status === 200) {
                    queueUpdates(res, token);
                }
            });
        } else {
            putBusc(event.data._id, updatedData).then(res => {
                if (res.status === 201) {
                    queueUpdates(res, token);
                }
            });
        }
    }, [context, postBusc, putBusc, getAccessTokenSilently]);

    const queueUpdates = useCallback(async (res, token) => {
        const endPoints = {
            "storage": "update_tagging",
            "virtual_cluster": "update_virtual_tagging",
            "switch": "update_switch_tagging",
            "database_instance": "update_database_tagging",
            "cloud_resource": "update_cloud_tagging",
            "esx_host": "update_esx_host_tagging"
        }
        const formData = new FormData();
        formData.append("context", context);
        formData.append("client_id", res.data.clientId);

        if (context === "storage") {
            formData.append("device_name", res.data.deviceName);
            formData.append("device_type_code", res.data.deviceTypeCode);
            formData.append("data_center", res.data.dataCenter ? res.data.dataCenter : '');
            formData.append("operational_category", res.data.operationalCategory ? res.data.operationalCategory : '');
            formData.append("application", res.data.application ? res.data.application : '');
            formData.append("classification", res.data.classification ? res.data.classification : '');
            formData.append("display_name", res.data.displayName ? res.data.displayName : '');
            formData.append("virtualized", res.data.virtualized ? res.data.virtualized : 0);
            formData.append("geography", res.data.geography ? res.data.geography : '');
            formData.append("company", res.data.company ? res.data.company : '');
            formData.append("cost_per_esx_host", res.data.costPerEsxHost ? res.data.costPerEsxHost : 0.00);
        } else if (context === "virtual_cluster") {
            formData.append("cluster_name", res.data.clusterName);
            formData.append("vcenter", res.data.vCenterName);
            formData.append("company", res.data.company ? res.data.company : '');
            formData.append("description", res.data.description ? res.data.description : '');
            formData.append("data_center", res.data.dataCenter ? res.data.dataCenter : '');
            formData.append("function", res.data.function ? res.data.function : '');
            formData.append("category", res.data.category ? res.data.category : '');
            formData.append("pct_calc_cpu", res.data.percentCPUCalc ? res.data.percentCPUCalc : 0.00);
            formData.append("pct_calc_memory", res.data.percentMemoryCalc ? res.data.percentMemoryCalc : 0.00);
            formData.append("pct_calc_disk", res.data.percentDiskCalc ? res.data.percentDiskCalc : 0.00);

            formData.append("pct_cpu_threshold", res.data.percentCPUThreshold ? res.data.percentCPUThreshold : 0.00);
            formData.append("pct_memory_threshold", res.data.percentMemoryThreshold ? res.data.percentMemoryThreshold : 0.00);
            formData.append("pct_disk_threshold", res.data.percentDiskThreshold ? res.data.percentDiskThreshold : 0.00);
        } else if (context === "switch") {
            formData.append("switch_name", res.data.switchName);
            formData.append("fabric", res.data.fabric ? res.data.fabric : '');
            formData.append("data_center", res.data.dataCenter ? res.data.dataCenter : '');
            formData.append("function", res.data.function ? res.data.function : '');
            formData.append("price_per_port_activation", res.data.pricePerPortActivation ? res.data.pricePerPortActivation : 0)
            formData.append("end_of_service_life", res.data.endOfServiceLife ? res.data.endOfServiceLife : '');
        } else if (context === "database_instance") {
            formData.append("instance", res.data.instanceName);
            formData.append("category", res.data.category ? res.data.category : ' ');
            formData.append("data_center", res.data.dataCenter ? res.data.dataCenter : ' ');
            formData.append("storage_type", res.data.storageType ? res.data.storageType : ' ');
        } else if (context === "cloud_resource") {
            formData.append("resource_name", res.data.resourceName);
            formData.append("category", res.data.category ? res.data.category : ' ');
            formData.append("subscription", res.data.subscription ? res.data.subscription : ' ');
            formData.append("geography", res.data.geography ? res.data.geography : ' ');
        } else if (context === "esx_host") {
            formData.append("esx_host_name", res.data.esxHostName);
            formData.append("cluster_name", res.data.clusterName);
            formData.append("vcenter", res.data.vCenterName);
            formData.append("end_of_service_life", res.data.endOfServiceLife ? res.data.endOfServiceLife : '');
            formData.append("end_of_support", res.data.endOfSupport ? res.data.endOfSupport : '');
        }

        await PostAuth(`/api/v6/upload/${endPoints[context]}`, formData, token);

    }, [context]);

    const onContextChange = ({value}) => {
        setParams({
            context: value
        });
    };

    const columnDefs = useMemo(() => {
        if (context === "storage") {
            return storageTaggingDefs;
        } else if (context === "virtual_cluster") {
            return virtualClusterTaggingDefs;
        } else if (context === "database_instance") {
            return databaseInstanceTaggingDefs;
        } else if (context === "cloud_resource") {
            return cloudResourceTaggingDefs;
        } else if (context === "switch") {
            return switchTaggingDefs;
        } else if (context === "esx_host") {
            return virtualEsxHostTaggingDefs;
        } else {
            return [];
        }
    }, [context]);

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Tagging</VsiH4>
            <VsiBubble>
                <VsiSelectLabel>
                    Context
                </VsiSelectLabel>
                <Nav
                    tabs
                >
                    {allTagContexts.map((tag) => (
                        <NavItem
                            key={`button-${tag.value}-context`} style={{cursor: 'pointer'}}>
                            <NavLink
                                active={context === tag.value}
                                disabled={context === tag.value}
                                onClick={() => onContextChange({value: tag.value})}
                            >
                                {tag.label}
                            </NavLink>
                        </NavItem>
                    ))}
                </Nav>
                <Grid data={buData}
                      isLoading={isLoading}
                      exportCsv={true}
                      onRowEditingStopped={onRowEditingStopped}
                      columnDefs={columnDefs}
                      gridText={"Double click a row to edit then press enter to commit changes."}/>
            </VsiBubble>
        </Card>
    );
});
